import { createRoot, type Root } from 'react-dom/client'
import { overlayRoot } from '../../lib/overlay'
import { WarningGlyph } from './states'

/**
 * Toasts confirm; they never ask. One at a time — a new one replaces the
 * current one and resets the clock, rather than stacking, because a column of
 * confirmations is a list of things to read and the point of a confirmation is
 * that you don't have to.
 *
 * Two tones, because a toast that looks the same whatever it says is a toast
 * that says nothing. "Two-factor is on" and "That code didn't work" arrived
 * identically — same tick, same ground — and the second one is not a
 * confirmation at all. Round two's §1d reserves the failure colour for
 * failures; this is one of the places it was never spent.
 *
 * A failure also stays up twice as long and is announced assertively: a
 * confirmation you miss costs nothing, and one you miss because it looked like
 * a confirmation and wasn't costs you the next five minutes wondering why
 * nothing happened.
 *
 * Deliberately not the older components/Toast.tsx, which stacks, carries an
 * action and a dismiss button. Both exist while the migration runs; this is the
 * one the redesign uses.
 */

export type ToastTone = 'done' | 'fail'

/**
 * The way back out of something already done — "Removed. · Undo".
 *
 * This is not the toast asking a question, which it still never does: the thing
 * has happened, and this is the escape hatch on it. It exists because three of
 * the project record's panels delete with an undo window, and the only toast
 * that could carry one was the old stacking `components/Toast.tsx` — whose host
 * the record *overlay* never mounted, so in the overlay those confirmations and
 * their undo rendered nothing at all.
 */
export interface ToastAction {
  label: string
  onClick: () => void
}

/**
 * How long each kind stays. A toast carrying an action stays as long as the
 * caller's undo window, because a toast that leaves before its own button
 * stops working is worse than no button — kept in step with ACTION_DISMISS_MS
 * on the old toast, which is what those callers time their delete against.
 */
const DISMISS_MS: Record<ToastTone, number> = { done: 2400, fail: 5200 }
const ACTION_MS = 5000

let root: Root | null = null
let container: HTMLElement | null = null
let timer: ReturnType<typeof setTimeout> | null = null

function teardown() {
  if (timer) clearTimeout(timer)
  timer = null
  root?.unmount()
  container?.remove()
  root = null
  container = null
}

export function showToast(message: string, tone: ToastTone = 'done', action?: ToastAction) {
  const host = overlayRoot()
  if (!host) return

  teardown()
  container = document.createElement('div')
  host.appendChild(container)
  root = createRoot(container)
  root.render(<Toast message={message} tone={tone} action={action} />)
  timer = setTimeout(teardown, action ? ACTION_MS : DISMISS_MS[tone])
}

export function dismissToast() {
  teardown()
}

function Toast({
  message,
  tone,
  action,
}: {
  message: string
  tone: ToastTone
  action?: ToastAction
}) {
  const fail = tone === 'fail'

  return (
    <div
      className="fr"
      // A failure is not a status line — it interrupts, so it is announced as
      // one rather than waiting for a gap in whatever the reader is doing.
      role={fail ? 'alert' : 'status'}
      aria-live={fail ? 'assertive' : 'polite'}
      style={{
        position: 'absolute',
        top: 74,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderRadius: 11,
        border: `1px solid ${fail ? 'var(--w-danger-line)' : 'var(--w-line-strong)'}`,
        // The frosted chrome ground either way, with the failure fill laid over
        // it: a toast that changes its whole colour reads as a different
        // component rather than as the same one in a worse mood. Two layers
        // rather than one blended colour, because --w-chrome is deliberately
        // translucent and blending would flatten the frost out of it.
        background: fail
          ? 'linear-gradient(var(--w-danger-fill),var(--w-danger-fill)),var(--w-chrome)'
          : 'var(--w-chrome)',
        boxShadow: 'var(--w-float)',
        // Long failure messages come straight from the server and are sentences,
        // not labels — a nowrap toast pushes them off both edges of the frame.
        maxWidth: 'min(520px,calc(100% - 48px))',
        // A toast with a button has to be clickable; one without must not eat
        // a click aimed at whatever it is floating over.
        pointerEvents: action ? 'auto' : 'none',
      }}
    >
      {/* The same warning mark AlertRow and Refusal use, rather than a second
          glyph meaning the same thing — one mark for "this did not work", so
          it is recognised rather than read. */}
      {fail ? (
        <WarningGlyph size={15} style={{ marginTop: 0 }} />
      ) : (
        <svg
          width="13"
          height="13"
          viewBox="0 0 12 12"
          fill="none"
          stroke="var(--color-action-ink)"
          strokeWidth="2.2"
          style={{ flex: 'none' }}
          aria-hidden="true"
        >
          <path d="M2.5 6.3 4.8 8.6 9.5 3.9" />
        </svg>
      )}
      <span
        style={{
          font: '600 12px/1.45 Manrope,sans-serif',
          color: fail ? 'var(--w-danger-ink)' : undefined,
        }}
      >
        {message}
      </span>

      {action && (
        <button
          type="button"
          className="tab"
          onClick={() => {
            action.onClick()
            teardown()
          }}
          style={{
            appearance: 'none',
            border: 0,
            margin: 0,
            padding: '3px 8px',
            borderRadius: 6,
            background: 'var(--w-fill)',
            font: '600 11px Manrope,sans-serif',
            color: 'var(--color-action-ink)',
            cursor: 'pointer',
            flex: 'none',
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  )
}
